import type { AnimationEventHandler } from 'react';
import React from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButton } from 'components/common/icon-button';
import CounterBadge from 'components/common/counter-badge';
import { useSessionStorageState, useUpdateEffect } from 'ahooks';
import { EventNames, track } from 'utils/analytics.utils';

interface Props {
  isRead: boolean;
  onClick: () => void;
}

/**
 * The animation sits on the wrapper rather than the control, so it rotates the whole affordance
 * including the unread dot.
 */
const wiggle = css`
  &.wiggle {
    animation: 1s wiggle ease;
    animation-iteration-count: 5;
  }

  @keyframes wiggle {
    0% {
      transform: rotate(-3deg);
    }
    20% {
      transform: rotate(20deg);
    }
    40% {
      transform: rotate(-15deg);
    }
    60% {
      transform: rotate(5deg);
    }
    90% {
      transform: rotate(-1deg);
    }
    100% {
      transform: rotate(0);
    }
  }
`;

export const AppNotificationsButton: React.FC<Props> = ({ isRead, onClick }) => {
  const [shouldWiggle, setShouldWiggle] = useSessionStorageState('should-app-notifications-button-wiggle', {
    defaultValue: true
  });

  useUpdateEffect(() => {
    if (!isRead) {
      setShouldWiggle(true);
    }
  }, [isRead, setShouldWiggle]);

  const onAnimationEnd: AnimationEventHandler<HTMLElement> = e => {
    if (e.target === e.currentTarget) {
      setShouldWiggle(false);
    }
  };

  const onAnimationStart: AnimationEventHandler<HTMLElement> = e => {
    if (e.target === e.currentTarget) {
      track(EventNames.IN_APP_WHATS_NEW_SHOW_NEW_ITEMS);
    }
  };

  const wiggleClass = shouldWiggle && !isRead ? 'wiggle' : '';

  /*
   * `whats-new-button` and the click handler stay on the wrapper, because that is what animates and a
   * click on the button inside bubbles up to it.
   */
  return (
    <WiggleBox
      data-e2e="whats-new-button"
      className={wiggleClass}
      onClick={onClick}
      onAnimationStart={onAnimationStart}
      onAnimationEnd={onAnimationEnd}>
      <CounterBadge withoutMargin dot={!isRead} offset={[-6, 6]}>
        <IconButton
          tooltipTitleId="whats.new"
          dataE2e="whats-new-icon"
          ariaLabel="What's new"
          icon={InboxOutlined}
          size="small"
        />
      </CounterBadge>
    </WiggleBox>
  );
};

// Sizing comes from the cluster's icon-button rule in topbar-view, which this and the theme toggle share.
const WiggleBox = styled.span`
  display: inline-flex;
  align-items: center;

  ${wiggle}
`;
